import React, { useState, useRef, useEffect } from 'react';
import './ModernChatInterface.css';

function ModernChatInterface({ 
  conversationId, 
  community, 
  placeholder = "Type your message...",
  onInputFocus,
  onInputBlur,
  showWelcomeMessage = true 
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Welcome message
  useEffect(() => {
    if (showWelcomeMessage && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Hi there! I'm your ${community.name} assistant. How can I help you today?`,
        timestamp: new Date()
      }]);
    }
  }, [showWelcomeMessage, community.name]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  // Smart suggestions based on input
  useEffect(() => {
    if (input.length > 2) {
      const inputLower = input.toLowerCase();
      const contextualSuggestions = [];
      
      if (inputLower.includes('food') || inputLower.includes('eat')) {
        contextualSuggestions.push('restaurants', 'pizza', 'coffee shops', 'delivery');
      }
      if (inputLower.includes('shop') || inputLower.includes('buy')) {
        contextualSuggestions.push('grocery stores', 'shopping centers', 'pharmacies');
      }
      if (inputLower.includes('doctor') || inputLower.includes('health')) {
        contextualSuggestions.push('walk-in clinics', 'hospitals', 'dentists');
      }
      
      setSuggestions(contextualSuggestions.slice(0, 3));
    } else {
      setSuggestions([]);
    }
  }, [input]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSuggestions([]);
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        role: 'assistant',
        content: `I understand you're asking about "${userMessage.content}" in ${community.name}. Let me help you with that...`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 1500);
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(input + ' ' + suggestion);
    inputRef.current?.focus();
    setSuggestions([]);
  };

  return (
    <div className="modern-chat-interface">
      {/* Messages Area */}
      <div className="messages-area">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.role}`}>
            <div className="message-avatar">
              {message.role === 'assistant' ? '🤖' : '👤'}
            </div>
            <div className="message-content">
              <div className="message-bubble">
                {message.content}
              </div>
              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="message assistant">
            <div className="message-avatar">🤖</div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="input-area">
        {suggestions.length > 0 && (
          <div className="suggestions-bar">
            <span className="suggestions-label">Suggestions:</span>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="chat-form">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              className="chat-input"
              onFocus={onInputFocus}
              onBlur={onInputBlur}
            />
            <button 
              type="submit" 
              className="send-button"
              disabled={!input.trim()}
            >
              <span className="send-icon">➤</span>
            </button>
          </div>
        </form>
        
        <div className="input-helper">
          <span className="helper-text">
            Press Enter to send • Shift+Enter for new line
          </span>
        </div>
      </div>
    </div>
  );
}

export default ModernChatInterface;